import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Snackbar,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import api from '../../services/api';

interface PlannedFeature {
  id: string;
  name: string;
  deployed: boolean;
  sortOrder: number;
}

interface QuietHoursConfig {
  enabled: boolean;
  startHour: number;
  endHour: number;
  timezone: string;
}

const SettingsPage: React.FC = () => {
  const [features, setFeatures] = useState<PlannedFeature[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [quietHours, setQuietHours] = useState<QuietHoursConfig>({
    enabled: false,
    startHour: 22,
    endHour: 7,
    timezone: 'Europe/London',
  });
  const [quietHoursLoading, setQuietHoursLoading] = useState(true);
  const [quietHoursSaving, setQuietHoursSaving] = useState(false);
  const [quietHoursSnackbar, setQuietHoursSnackbar] = useState(false);

  useEffect(() => {
    api.get('/features')
      .then((res) => setFeatures(res.data))
      .catch(() => {})
      .finally(() => setFeaturesLoading(false));

    api.get('/notification-engine/settings')
      .then((res) => setQuietHours(res.data.quietHours))
      .catch(() => {})
      .finally(() => setQuietHoursLoading(false));
  }, []);

  const handleSaveQuietHours = async () => {
    setQuietHoursSaving(true);
    try {
      await api.put('/notification-engine/settings', { quietHours });
      setQuietHoursSnackbar(true);
    } catch {
      // Error handling
    } finally {
      setQuietHoursSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Application configuration and information
      </Typography>

      <Alert severity="info" sx={{ mb: 4 }}>
        Settings are currently read-only. Full configuration will be available after backend integration.
      </Alert>

      <Grid container spacing={3}>
        {/* App Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <InfoIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Application Information
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Application Name
                  </Typography>
                  <Typography variant="body1">Team Jak Events</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Version
                  </Typography>
                  <Typography variant="body1">1.0.0</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Environment
                  </Typography>
                  <Typography variant="body1">Development</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Build Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date().toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Backend Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <StorageIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Backend Status
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    API Status
                  </Typography>
                  <Typography variant="body1" color="success.main">
                    Database Data
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Database
                  </Typography>
                  <Typography variant="body1" color="success.main">
                    Database Connected (Azure SQL Server)
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    CRM Integration
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Pending (Dynamics 365)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Security */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Security & Authentication
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Authentication Provider
                  </Typography>
                  <Typography variant="body1" color="warning.main">
                    Mock Auth (Development)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Azure AD B2C planned for production
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Session Management
                  </Typography>
                  <Typography variant="body1">Local Storage</Typography>
                  <Typography variant="caption" color="text.secondary">
                    JWT tokens planned for production
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Admin Authentication
                  </Typography>
                  <Typography variant="body1" color="warning.main">
                    Mock Auth (Development)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Azure AD planned for production
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Quiet Hours */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <NotificationsOffIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Quiet Hours
                </Typography>
              </Box>

              {quietHoursLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={quietHours.enabled}
                        onChange={(e) => setQuietHours({ ...quietHours, enabled: e.target.checked })}
                      />
                    }
                    label="Enable quiet hours"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Notifications will be held and released after quiet hours end. Auth emails (login codes) are never delayed.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Start Hour (24h)"
                        value={quietHours.startHour}
                        onChange={(e) => setQuietHours({ ...quietHours, startHour: parseInt(e.target.value) || 0 })}
                        inputProps={{ min: 0, max: 23 }}
                        disabled={!quietHours.enabled}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="End Hour (24h)"
                        value={quietHours.endHour}
                        onChange={(e) => setQuietHours({ ...quietHours, endHour: parseInt(e.target.value) || 0 })}
                        inputProps={{ min: 0, max: 23 }}
                        disabled={!quietHours.enabled}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    fullWidth
                    size="small"
                    label="Timezone"
                    value={quietHours.timezone}
                    onChange={(e) => setQuietHours({ ...quietHours, timezone: e.target.value })}
                    disabled={!quietHours.enabled}
                    helperText="e.g. Europe/London, America/New_York"
                  />
                  <Button
                    variant="contained"
                    onClick={handleSaveQuietHours}
                    disabled={quietHoursSaving}
                    sx={{ alignSelf: 'flex-start', bgcolor: '#00b7ed', '&:hover': { bgcolor: '#0095c2' } }}
                  >
                    {quietHoursSaving ? 'Saving...' : 'Save Quiet Hours'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Future Features */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Planned Features
              </Typography>
              {featuresLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {features.map((feature) => (
                    <Grid item xs={12} sm={6} md={4} key={feature.id}>
                      <Box
                        sx={{
                          p: 1.5,
                          backgroundColor: feature.deployed ? '#e8f5e9' : '#f5f5f5',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {feature.deployed && (
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        )}
                        <Typography variant="body2">{feature.name}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={quietHoursSnackbar}
        autoHideDuration={3000}
        onClose={() => setQuietHoursSnackbar(false)}
        message="Quiet hours settings saved"
      />
    </Box>
  );
};

export default SettingsPage;
